import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'

/**
 * Endocrine subsection adapter
 * Handles endocrine system assessment and menstrual cycle tracking (5 fields)
 */
export class EndocrineAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Endocrine assessment fields
    const endocrineWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL
    )?.at(0)

    const menstrualCycle = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE
    )?.at(0)

    const menstrualDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE
    )?.at(0)

    const endocrineNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.ENDOCRINE_WNL]: endocrineWnl,
      [RecertFormField.ENDOCRINE_MENSTRUAL_CYCLE]: menstrualCycle,
      [RecertFormField.ENDOCRINE_MENSTRUAL_DATE]: menstrualDate,
      [RecertFormField.ENDOCRINE_NOTES]: endocrineNotes,
      [RecertFormField.ENDOCRINE_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.ENDOCRINE_WNL)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_WNL,
        value: this.getFormDataField(RecertFormField.ENDOCRINE_WNL)
      })
    }

    if (this.formDataFieldExists(RecertFormField.ENDOCRINE_MENSTRUAL_CYCLE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_CYCLE,
        value: this.getFormDataField(RecertFormField.ENDOCRINE_MENSTRUAL_CYCLE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.ENDOCRINE_MENSTRUAL_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_MENSTRUAL_DATE,
        value: this.getFormDataField(RecertFormField.ENDOCRINE_MENSTRUAL_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.ENDOCRINE_NOTES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_ENDOCRINE_NOTES,
        value: this.getFormDataField(RecertFormField.ENDOCRINE_NOTES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.ENDOCRINE_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_ENDOCRINE_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.ENDOCRINE_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
